import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, X } from 'lucide-react';
import { useLaunchDashboardStore } from '@/stores/launch-dashboard-store';
import { useMapStore } from '@/stores/map-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { ICON_RAIL_WIDTH } from '@/constants/layout';
import type { UpcomingLaunch } from '@vtak/mod-space/client';
import { useUpcomingLaunches, usePreviousLaunches, useLaunchStats } from '@/queries/use-launch-queries';
import { ActiveMissionCard } from './active-mission-card';
import { MissionList } from './mission-list';
import { LaunchStatsCard } from './launch-stats';
import { LaunchDetailModal } from './launch-detail-modal';
import { MissionFilters } from './mission-filters';
import { filterLaunches, distinctValues, withFocus, EMPTY_FILTER, type LaunchFilter } from './filter-launches';

/** Full-screen Launch Dashboard overlay (live launches, countdown, stats). */
export function LaunchDashboard(): React.JSX.Element {
  const open = useLaunchDashboardStore((s) => s.open);
  const close = useLaunchDashboardStore((s) => s.close);
  const launchFocus = useWorkspaceStore((s) => s.workspace?.launchFocus);
  const [selected, setSelected] = useState<UpcomingLaunch | null>(null);
  const [filter, setFilter] = useState<LaunchFilter>(EMPTY_FILTER);
  // The unit's saved focus is the default lens whenever one is configured.
  const [focusActive, setFocusActive] = useState(true);

  const focusSites = launchFocus?.sites ?? [];
  const focusConfigured = focusSites.length > 0 || (launchFocus?.rockets.length ?? 0) > 0;
  // Site-scoped pool: when the focus lens is on and names sites, the server
  // queries LL2 by location, so the pool is EVERY upcoming launch at those
  // sites — not whatever slice of the global schedule happens to match.
  const siteScoped = focusConfigured && focusActive && focusSites.length > 0;

  // Cached + polled (60s) only while the dashboard is open; re-opening is instant from cache.
  const upcoming = useUpcomingLaunches(open, siteScoped ? focusSites : []).data ?? [];
  const previous = usePreviousLaunches(open).data ?? [];
  const stats = useLaunchStats(open).data ?? { totalThisYear: 0, successesThisYear: 0 };

  // Focus stays in the client filter too, as a safety net over the scoped
  // pool (and it is what applies a rockets-only focus to the global pool).
  const effectiveFilter = useMemo(
    () => (focusConfigured && focusActive ? withFocus(filter, launchFocus) : filter),
    [filter, launchFocus, focusConfigured, focusActive],
  );

  const all = useMemo(() => [...upcoming, ...previous], [upcoming, previous]);
  const providers = useMemo(() => distinctValues(all, (l) => l.provider), [all]);
  const orbits = useMemo(() => distinctValues(all, (l) => l.orbit), [all]);
  // The pool as the focus lens sees it, without transient search/dropdown filters.
  const focusPool = useMemo(
    () => (focusConfigured && focusActive ? filterLaunches(upcoming, withFocus(EMPTY_FILTER, launchFocus)) : upcoming),
    [upcoming, launchFocus, focusConfigured, focusActive],
  );
  const visibleUpcoming = useMemo(() => filterLaunches(upcoming, effectiveFilter), [upcoming, effectiveFilter]);
  const filteredUpcoming = useMemo(() => visibleUpcoming.slice(1), [visibleUpcoming]);
  const filteredPrevious = useMemo(() => filterLaunches(previous, effectiveFilter), [previous, effectiveFilter]);
  // A hidden count is only meaningful when focus filters the GLOBAL pool
  // client-side (rockets-only focus); a site-scoped pool hides nothing.
  const hiddenByFocus = siteScoped ? 0 : upcoming.length - focusPool.length;

  function flyToLaunch(launch: UpcomingLaunch): void {
    if (launch.lat === null || launch.lon === null) return;
    useMapStore.getState().setFlyTo({ lat: launch.lat, lng: launch.lon, zoom: 14 });
    setSelected(null);
    close();
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="absolute z-[1200] flex flex-col bg-[#0b0e14]/96 backdrop-blur-sm"
          style={{ top: 0, right: 0, bottom: 0, left: ICON_RAIL_WIDTH }}
        >
          <header className="flex items-center gap-2 border-b border-echo-border px-6 py-3">
            <Rocket className="h-5 w-5 text-echo-yellow" />
            <h1 className="flex-1 font-chakra text-base font-semibold uppercase tracking-[0.18em] text-foreground">
              Launch Dashboard
            </h1>
            <button type="button" onClick={close} aria-label="Close dashboard" className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
            <MissionFilters
              filter={filter}
              providers={providers}
              orbits={orbits}
              onChange={setFilter}
              focusConfigured={focusConfigured}
              focusActive={focusActive}
              focusSites={siteScoped ? focusSites : []}
              hiddenByFocus={hiddenByFocus}
              onToggleFocus={() => setFocusActive((active) => !active)}
            />
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto lg:grid-cols-2">
              <div className="flex min-h-0 flex-col gap-4">
                {visibleUpcoming[0] ? <ActiveMissionCard launch={visibleUpcoming[0]} /> : <Placeholder text="Loading next mission…" />}
                <MissionList
                  title="Upcoming Missions"
                  launches={filteredUpcoming}
                  onSelect={setSelected}
                  emptyText={
                    visibleUpcoming.length === 1
                      ? 'The only launch in this view is featured above as the next mission'
                      : undefined
                  }
                />
              </div>
              <div className="flex min-h-0 flex-col gap-4">
                <LaunchStatsCard
                  stats={stats}
                  upcoming={focusPool}
                  scope={focusConfigured && focusActive ? 'Unit focus' : 'All launches'}
                />
                <MissionList title="Previous Missions" launches={filteredPrevious} onSelect={setSelected} />
              </div>
            </div>
          </div>

          {selected && (
            <LaunchDetailModal launch={selected} onClose={() => setSelected(null)} onGoToLocation={flyToLaunch} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Simple placeholder card. */
function Placeholder({ text }: { text: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center rounded-lg border border-echo-border bg-echo-grey/80 p-8 text-xs text-muted-foreground">
      {text}
    </div>
  );
}
